import { isValidCalendarDate } from './calendarDate'
import { getCurrentFiscalYear, getFiscalYear } from './fiscalYear'

const GSA_LOOKBACK_YEARS = 2
const GSA_PUBLISH_MONTH = 8
const GSA_PUBLISH_DAY = 15

export type FiscalYearRange = {
  minFY: number
  maxFY: number
}

export const getAllowedFiscalYearRange = (date: Date = new Date()): FiscalYearRange => {
  const currentFY = getCurrentFiscalYear(date)
  let maxFY = currentFY

  const publishThreshold = new Date(date.getFullYear(), GSA_PUBLISH_MONTH - 1, GSA_PUBLISH_DAY)
  if (date >= publishThreshold) {
    maxFY = currentFY + 1
  }

  return {
    maxFY,
    minFY: currentFY - GSA_LOOKBACK_YEARS,
  }
}

export const isFiscalYearAllowed = (fy: number, date: Date = new Date()): boolean => {
  const { minFY, maxFY } = getAllowedFiscalYearRange(date)
  return fy >= minFY && fy <= maxFY
}

export const isCalendarDateAllowed = (month: number, day: number, year: number, date: Date = new Date()): boolean => {
  if (!isValidCalendarDate(month, day, year)) {
    return false
  }
  return isFiscalYearAllowed(getFiscalYear(month, year), date)
}

export const getValidYearsForMonth = (month: number, date: Date = new Date()): number[] => {
  const { minFY, maxFY } = getAllowedFiscalYearRange(date)
  const years: number[] = []
  for (let year = minFY - 1; year <= maxFY; year++) {
    const fy = getFiscalYear(month, year)
    if (fy >= minFY && fy <= maxFY) {
      years.push(year)
    }
  }
  return years
}

export const getValidMonthsForYear = (year: number, date: Date = new Date()): number[] => {
  const { minFY, maxFY } = getAllowedFiscalYearRange(date)
  const months: number[] = []
  for (let month = 1; month <= 12; month++) {
    const fy = getFiscalYear(month, year)
    if (fy >= minFY && fy <= maxFY) {
      months.push(month)
    }
  }
  return months
}

export const clampToValidDate = (
  month: number,
  day: number,
  year: number,
  date: Date = new Date(),
): { month: number; day: number; year: number } => {
  let nextMonth = month
  let nextYear = year

  const validMonths = getValidMonthsForYear(nextYear, date)
  if (!validMonths.includes(nextMonth)) {
    nextMonth = validMonths[validMonths.length - 1] ?? nextMonth
  }

  const validYears = getValidYearsForMonth(nextMonth, date)
  if (!validYears.includes(nextYear)) {
    nextYear = validYears[validYears.length - 1] ?? nextYear
  }

  const validMonthsAfterYear = getValidMonthsForYear(nextYear, date)
  if (!validMonthsAfterYear.includes(nextMonth)) {
    nextMonth = validMonthsAfterYear[validMonthsAfterYear.length - 1] ?? nextMonth
  }

  const nextDay = Math.min(day, new Date(nextYear, nextMonth, 0).getDate())

  return { day: nextDay, month: nextMonth, year: nextYear }
}
